import { useTranslation } from 'react-i18next'
import { ArrowLeftRightIcon, EditIcon, RefreshCwIcon, SpeechIcon, Trash, Undo2Icon } from 'lucide-react'
import { RxDocument } from 'rxdb'
import { Button } from 'ui/button'
import { Skeleton } from 'ui/skeleton'

import { LinkButton } from '~/components/link-button'
import { TapEffect } from '~/components/tap-effect'
import { Card } from '~/db/types'
import { useCardDeletionRevert } from '~/hooks/use-card-deletion-revert'
import { IS_VOICE_SUPPORTED, useVoice } from '~/hooks/use-voice'
import { useSettingsStore } from '~/stores/settings'

type CardsMenuBarProps = {
  activeCard: RxDocument<Card>
  isRevertLastCard: boolean
  onRefreshStack: () => void
  revertLastCard: () => void
}

export const CardsMenuBar = ({ activeCard, onRefreshStack, revertLastCard, isRevertLastCard }: CardsMenuBarProps) => {
  const learnLanguage = useSettingsStore(state => state.learnLanguage)
  const voice = useVoice(learnLanguage.value ?? 'en')
  const voicesAvailability = useSettingsStore(state => state.voicesAvailability)
  const patchSettings = useSettingsStore(state => state.patchSettings)
  const cardsFront = useSettingsStore(state => state.cardsFront)
  const { t } = useTranslation()
  const removeCard = useCardDeletionRevert()

  const isVoiceLoading = voicesAvailability[learnLanguage.value] && !voice

  const tts = () => {
    if (!voice) return

    const utterThis = new SpeechSynthesisUtterance(activeCard.learnText)
    utterThis.voice = voice
    utterThis.pitch = 1
    utterThis.rate = 1
    speechSynthesis.speak(utterThis)
  }

  return (
    <div className="flex justify-between gap-2">
      <div className="flex flex-1 gap-2">
        {isRevertLastCard && (
          <Button aria-label={t('return previous card')} onClick={revertLastCard} size="iconLg" variant="icon">
            <Undo2Icon />
          </Button>
        )}
      </div>
      <div className="flex flex-wrap justify-center gap-2">
        <TapEffect>
          <LinkButton
            aria-label={t('edit current card')}
            search={{ activeCardId: activeCard.id }}
            size="iconLg"
            to="/list"
            variant="icon"
          >
            <EditIcon />
          </LinkButton>
        </TapEffect>
        <TapEffect>
          <Button aria-label={t('refresh stack')} onClick={() => onRefreshStack()} size="iconLg" variant="icon">
            <RefreshCwIcon />
          </Button>
        </TapEffect>
        <TapEffect>
          <Button
            aria-label={t('reverse front side text')}
            onClick={() => {
              patchSettings({
                cardsFront: cardsFront === 'learn' ? 'source' : 'learn',
              })
            }}
            size="iconLg"
            variant="icon"
          >
            <ArrowLeftRightIcon />
          </Button>
        </TapEffect>
        {IS_VOICE_SUPPORTED && voice && (
          <TapEffect>
            <Button aria-label={t('pronounce the learn text')} onClick={tts} size="iconLg" variant="icon">
              <SpeechIcon />
            </Button>
          </TapEffect>
        )}
        {IS_VOICE_SUPPORTED && isVoiceLoading && <Skeleton className="size-12 rounded-full" />}
      </div>
      <div className="flex flex-1 justify-end">
        <TapEffect>
          <Button
            aria-label={t('delete active card')}
            onClick={() => removeCard(activeCard)}
            size="iconLg"
            variant="iconDestructive"
          >
            <Trash />
          </Button>
        </TapEffect>
      </div>
    </div>
  )
}
